/**
 * MIDI Service - Web MIDI API Integration
 * Handles connection to LoopMIDI and Looper Plus device
 */

import { WebMidi } from 'webmidi';
import errorLog from './errorLog';

class MIDIService {
    constructor() {
        this.enabled = false;
        this.inputs = [];
        this.outputs = [];
        this.selectedInput = null;
        this.selectedOutput = null;
        this.listeners = new Map();
    }

    /**
     * Initialize Web MIDI API
     */
    async init() {
        try {
            await WebMidi.enable({ sysex: true });
            this.enabled = true;
            this.refreshPorts();

            // Keep port lists current when devices are hot-plugged/unplugged,
            // so users don't have to press Refresh manually. Bind once — a
            // second init() must not stack duplicate WebMidi listeners.
            if (!this._hotPlugBound) {
                WebMidi.addListener('connected', () => this._onPortsChanged());
                WebMidi.addListener('disconnected', () => this._onPortsChanged());
                this._hotPlugBound = true;
            }

            console.log('✅ Web MIDI API enabled successfully');
            return { success: true };
        } catch (error) {
            console.error('❌ Web MIDI API initialization failed:', error);
            errorLog.capture(error, 'midi', { op: 'init' });
            return { success: false, error: error.message };
        }
    }

    /**
     * Handle hot-plug events: refresh cached ports and notify subscribers.
     */
    _onPortsChanged() {
        this.refreshPorts();
        this.notifyListeners('portschanged', {
            inputs: this.inputs,
            outputs: this.outputs
        });
    }

    /**
     * Refresh available MIDI ports
     */
    refreshPorts() {
        if (!this.enabled) return;

        this.inputs = WebMidi.inputs.map(input => ({
            id: input.id,
            name: input.name,
            manufacturer: input.manufacturer,
            state: input.state
        }));

        this.outputs = WebMidi.outputs.map(output => ({
            id: output.id,
            name: output.name,
            manufacturer: output.manufacturer,
            state: output.state
        }));

        // console.log(`📡 Found ${this.inputs.length} MIDI inputs, ${this.outputs.length} outputs`);
    }

    /**
     * Connect to a specific MIDI port
     */
    connect(inputId, outputId) {
        try {
            // Resolve BOTH ports before touching state: attaching the input
            // before validating the output used to leave orphaned listeners
            // behind on partial failure, and a retry stacked duplicates.
            const input = inputId ? WebMidi.getInputById(inputId) : null;
            const output = outputId ? WebMidi.getOutputById(outputId) : null;

            const missing = [];
            if (inputId && !input) missing.push(`input "${inputId}"`);
            if (outputId && !output) missing.push(`output "${outputId}"`);

            if (missing.length > 0) {
                // Device vanished between refresh and connect - don't fake success.
                this.refreshPorts();
                return {
                    success: false,
                    error: `MIDI port not found (${missing.join(', ')}). The device may have disconnected — refresh ports and try again.`
                };
            }

            if (input) {
                // Clear listeners from any previous connection so retries never
                // register duplicates.
                input.removeListener();
                this.selectedInput = input;
                this.setupInputListeners(input);
                // console.log(`🎹 Connected to input: ${input.name}`);
            }

            if (output) {
                this.selectedOutput = output;
                // console.log(`🎵 Connected to output: ${output.name}`);
            }

            return { success: true };
        } catch (error) {
            console.error('Connection error:', error);
            errorLog.capture(error, 'midi', { op: 'connect' });
            return { success: false, error: error.message };
        }
    }

    /**
     * Setup listeners for incoming MIDI messages
     */
    setupInputListeners(input) {
        // Note On
        input.addListener('noteon', e => {
            this.notifyListeners('noteon', {
                note: e.note.number,
                velocity: e.rawVelocity,
                channel: e.message.channel,
                timestamp: e.timestamp
            });
        });

        // Note Off
        input.addListener('noteoff', e => {
            this.notifyListeners('noteoff', {
                note: e.note.number,
                channel: e.message.channel,
                timestamp: e.timestamp
            });
        });

        // Control Change
        input.addListener('controlchange', e => {
            this.notifyListeners('controlchange', {
                controller: e.controller.number,
                value: e.value,
                channel: e.message.channel,
                timestamp: e.timestamp
            });
        });

        // Program Change
        input.addListener('programchange', e => {
            this.notifyListeners('programchange', {
                program: e.value,
                channel: e.message.channel,
                timestamp: e.timestamp
            });
        });
    }

    /**
     * Send MIDI Note On
     */
    /**
     * Internal method to send MIDI messages safely
     */
    _send(sendFn, successLog, errorLabel) {
        if (!this.selectedOutput) {
            // Only log warning if actually trying to send
            if (this.enabled) console.warn('No MIDI output selected');
            return false;
        }

        try {
            sendFn();
            // Verbose logging disabled for production
            // if (successLog) console.log(successLog);
            return true;
        } catch (error) {
            // NOTE: param renamed from `errorLog` to `errorLabel` so it no longer
            // shadows the imported errorLog collector below.
            console.error(errorLabel, error);
            errorLog.capture(error, 'midi', { op: 'send', detail: errorLabel });
            return false;
        }
    }

    /**
     * Send MIDI Note On
     *
     * IMPORTANT (webmidi 3): the documented `{ velocity }` option is a
     * deprecated shim interpreted as a 0-1 float. Passing raw 0-127 values
     * wraps into garbage on hardware (127 arrives as ~1). Use `rawAttack`.
     */
    sendNoteOn(note, velocity = 127, channel = 1) {
        return this._send(
            () => this.selectedOutput.sendNoteOn(note, { rawAttack: velocity, channels: channel }),
            null,
            'Error sending Note On:'
        );
    }

    /**
     * Send MIDI Note Off
     */
    sendNoteOff(note, channel = 1) {
        return this._send(
            () => this.selectedOutput.sendNoteOff(note, { rawRelease: 0, channels: channel }),
            null,
            'Error sending Note Off:'
        );
    }

    /**
     * Send MIDI Control Change
     */
    sendControlChange(controller, value, channel = 1) {
        return this._send(
            () => this.selectedOutput.sendControlChange(controller, value, { channels: channel }),
            null,
            'Error sending CC:'
        );
    }

    /**
     * Send MIDI Program Change
     */
    sendProgramChange(program, channel = 1) {
        return this._send(
            () => this.selectedOutput.sendProgramChange(program, { channels: channel }),
            null,
            'Error sending Program Change:'
        );
    }

    /**
     * Add listener for MIDI events
     */
    addListener(eventType, callback) {
        if (!this.listeners.has(eventType)) {
            this.listeners.set(eventType, []);
        }
        this.listeners.get(eventType).push(callback);
    }

    /**
     * Remove listener
     */
    removeListener(eventType, callback) {
        if (this.listeners.has(eventType)) {
            const callbacks = this.listeners.get(eventType);
            const index = callbacks.indexOf(callback);
            if (index > -1) {
                callbacks.splice(index, 1);
            }
        }
    }

    /**
     * Notify all listeners of an event
     */
    notifyListeners(eventType, data) {
        if (this.listeners.has(eventType)) {
            this.listeners.get(eventType).forEach(callback => callback(data));
        }
    }

    /**
     * Disconnect from MIDI ports
     */
    disconnect() {
        if (this.selectedInput) {
            this.selectedInput.removeListener();
            this.selectedInput = null;
        }
        this.selectedOutput = null;
        console.log('🔌 Disconnected from MIDI');
    }

    /**
     * Find likely Looper ports (Looper Plus, HeadRush, Sheeran)
     */
    findLooperPorts() {
        // Keywords to look for in device names
        const keywords = ['looper', 'sheeran', 'headrush', 'prime'];

        const input = this.inputs.find(i =>
            keywords.some(k => i.name.toLowerCase().includes(k)) &&
            i.state === 'connected'
        );

        const output = this.outputs.find(o =>
            keywords.some(k => o.name.toLowerCase().includes(k)) &&
            o.state === 'connected'
        );

        return {
            input: input ? input.id : null,
            output: output ? output.id : null,
            name: input ? input.name : (output ? output.name : null)
        };
    }
}

// Export singleton instance
export const midiService = new MIDIService();
export default midiService;
